import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button3D } from "@/components/ui/button-3d";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, CheckCircle, Loader2, MessageSquareHeart, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Reason =
  | "bug"
  | "missing_feature"
  | "complicated"
  | "price"
  | "just_testing"
  | "other";

const REASONS: { value: Reason; label: string }[] = [
  { value: "bug", label: "Está com bug" },
  { value: "missing_feature", label: "Faltou uma funcionalidade" },
  { value: "complicated", label: "É complicado de usar" },
  { value: "price", label: "Preço" },
  { value: "just_testing", label: "Só estava testando" },
  { value: "other", label: "Outro" },
];

export default function ChurnFeedbackPage() {
  const navigate = useNavigate();
  const [reason, setReason] = useState<Reason | "">("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.warning("Escolha um motivo para continuar");
      return;
    }

    setSubmitting(true);

    try {
      const platform = Capacitor.getPlatform();

      let appVersion = "web";
      if (Capacitor.isNativePlatform()) {
        try {
          const info = await CapacitorApp.getInfo();
          appVersion = info.version;
        } catch (err) {
          console.warn("Falha ao obter versão do app:", err);
        }
      }

      // `user_id` NÃO é mais enviado: a função deriva a identidade do token de
      // sessão (que o `invoke` anexa sozinho quando há sessão). Mandar o id no
      // corpo permitia atribuir feedback a qualquer conta — ver o comentário em
      // supabase/functions/churn-feedback/index.ts.
      const { error } = await supabase.functions.invoke("churn-feedback", {
        body: {
          reason,
          details: details.trim() || undefined,
          platform,
          app_version: appVersion,
        },
      });

      if (error) throw error;

      setSent(true);
    } catch (err) {
      console.error("Erro ao enviar feedback:", err);
      toast.error("Não conseguimos enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <MainLayout>
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-xl font-semibold">Obrigado pelo feedback!</h2>
            <p className="text-muted-foreground">
              Sua resposta ajuda o Orçamigo a melhorar para todo mundo.
            </p>
            <Button3D color="gray" onClick={() => navigate("/dashboard")} className="w-full">
              Voltar ao app
            </Button3D>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 p-3 rounded-full bg-orange-100">
            <MessageSquareHeart className="h-8 w-8 text-orange-600" />
          </div>
          <CardTitle>Vai desinstalar? Ajude a melhorar o Orçamigo</CardTitle>
          <CardDescription>
            Leva 10 segundos e vai direto para quem desenvolve o app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Qual o principal motivo?</Label>
            <RadioGroup
              value={reason}
              onValueChange={(v) => setReason(v as Reason)}
              className="space-y-2"
            >
              {REASONS.map((r) => (
                <div
                  key={r.value}
                  className="flex items-center space-x-2 rounded-lg border p-3"
                >
                  <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                  <Label
                    htmlFor={`reason-${r.value}`}
                    className="flex-1 cursor-pointer text-sm font-normal"
                  >
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details" className="text-sm font-medium">
              Quer dar mais detalhes? (opcional)
            </Label>
            <Textarea
              id="details"
              placeholder="Conte o que não funcionou, o que faltou, o que atrapalhou..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              maxLength={2000}
            />
          </div>

          <div className="space-y-2">
            <Button3D
              onClick={handleSubmit}
              disabled={submitting || !reason}
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Enviar feedback
                </>
              )}
            </Button3D>
            <Button3D
              color="gray"
              className="w-full"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Cancelar
            </Button3D>
          </div>
        </CardContent>
      </Card>
    </MainLayout>
  );
}
